import { useEffect, useRef, useState } from "react";

import { FullscriptCategory } from "@shared/components/CatalogSearch/Filters/FullscriptCategory/FullscriptCategory";
import { useFlippers } from "@shared/context/FlippersContext";
import { useAllFavoritesInfoSharedQuery } from "@shared/data/queries/AllFavoritesInfoQuery/AllFavoritesInfo.query.generated";
import {
  type CatalogContextualFiltersV2SharedQuery,
  useCatalogContextualFiltersV2SharedQueryLazyQuery,
} from "@shared/data/queries/CatalogContextualFilters/CatalogContextualFiltersV2.query.generated";
import { useFavoriteCategoriesInfoQuery } from "@shared/data/queries/FavoriteCategoriesInfoQuery/FavoriteCategoriesInfo.query";
import { useCatalogHideBrandsCheck } from "@shared/hooks/useCatalogHideBrandsCheck/useCatalogHideBrandsCheck";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { catalogTypeVar } from "@shared/reactiveVar/useCatalogType/useCatalogType.store";
import { isFullscriptJsVar } from "@shared/reactiveVar/useIsFullscriptJs/useIsFullscriptJs.store";
import { isInOfficeCheckoutVar } from "@shared/reactiveVar/useIsInOfficeCheckoutVar/useIsInOfficeCheckoutVar.store";
import { useSearchSessionIdVar } from "@shared/reactiveVar/useSearchSessionId/useSearchSessionId.store";
import type { OmitEmptyObject } from "@shared/types/OmitEmptyObject";

import { Allergens } from "../Filters/Allergens/Allergens";
import { Availability } from "../Filters/Availability/Availability";
import { Brands } from "../Filters/Brands/Brands";
import { Certifications } from "../Filters/Certifications/Certifications";
import { CurrentOffers } from "../Filters/CurrentOffers/CurrentOffers";
import { Demographics } from "../Filters/Demographics/Demographics";
import { Favorites } from "../Filters/Favorites/Favorites";
import { Form } from "../Filters/Form/Form";
import { FullscriptTested } from "../Filters/FullscriptTested/FullscriptTested";
import { HealthConditions } from "../Filters/HealthConditions/HealthConditions";
import { ExcludeIngredients } from "../Filters/Ingredients/ExcludeIngredients/ExcludeIngredients";
import { IncludeIngredients } from "../Filters/Ingredients/IncludeIngredients/IncludeIngredients";
import { PersonalCare } from "../Filters/PersonalCare/PersonalCare";
import { SupplementTypes } from "../Filters/SupplementTypes/SupplementTypes";

interface Props {
  isDrawer: boolean;
  isBold: boolean;
  isCyberMondayEnabled: boolean;
  logSearchApplyFilterEvent: (entityName: string, entityType: string) => void;
}

const ClinicFiltersContent = ({
  isDrawer,
  isBold,
  isCyberMondayEnabled,
  logSearchApplyFilterEvent,
}: Props) => {
  const [isEurekaContextualFiltersEnabled] = useFlippers("eureka_contextual_filters");

  const { isUnauthenticated, isPatient } = useSharedGlobalConfig();
  const { isOnlyShowFavorites, activeCategories } = useCatalogSearch();
  const isFullscriptJS = isFullscriptJsVar();
  const isInOfficeCheckout = isInOfficeCheckoutVar();
  const catalogType = catalogTypeVar();
  const isDtPOrWholesaleCatalog = catalogType.type === "DTP" || catalogType.type === "Wholesale";

  const currentSearchSessionId = useRef<string>("");
  const searchSessionId = useSearchSessionIdVar();

  const [favoritedCount, setFavoritedCount] = useState(0);
  const [activeFavoriteFilterCount, setActiveFavoriteFilterCount] = useState(0);

  const [contextualDataCacheV2, setContextualDataCacheV2] = useState<
    | OmitEmptyObject<CatalogContextualFiltersV2SharedQuery["viewer"]>["contextualFilters"]
    | undefined
  >(undefined);

  const showOldFavorites = isUnauthenticated || isPatient || isFullscriptJS || isInOfficeCheckout;

  const isContextual = isEurekaContextualFiltersEnabled && isDtPOrWholesaleCatalog;

  const [
    getCatalogContextualFiltersV2,
    { data: contextualFilterDataV2, loading: contextualFilterLoadingV2 },
  ] = useCatalogContextualFiltersV2SharedQueryLazyQuery();

  useEffect(() => {
    if (
      isContextual &&
      !contextualFilterLoadingV2 &&
      contextualFilterDataV2?.viewer?.contextualFilters
    ) {
      setContextualDataCacheV2(contextualFilterDataV2?.viewer?.contextualFilters);
    }
  }, [contextualFilterDataV2, contextualFilterLoadingV2]);

  const { data: categoriesData } = useFavoriteCategoriesInfoQuery({
    variables: {
      first: 200,
    },
    fetchPolicy: "cache-and-network",
    skip: showOldFavorites,
  });

  const { data: allFavoritesData } = useAllFavoritesInfoSharedQuery({
    fetchPolicy: "cache-and-network",
    skip: showOldFavorites,
  });

  useEffect(() => {
    if (
      isEurekaContextualFiltersEnabled &&
      isDtPOrWholesaleCatalog &&
      searchSessionId !== null &&
      currentSearchSessionId.current !== searchSessionId
    ) {
      currentSearchSessionId.current = searchSessionId;

      void getCatalogContextualFiltersV2({
        variables: {
          searchSessionId,
          dosageFormatSort: [{ field: "SEARCH_POPULARITY", direction: "DESC" }],
          allergenSort: [{ field: "SEARCH_POPULARITY", direction: "DESC" }],
          populationSort: [{ field: "SEARCH_POPULARITY", direction: "DESC" }],
          thirdPartyCertificationSort: [{ field: "SEARCH_POPULARITY", direction: "DESC" }],
        },
      });
    }
  }, [searchSessionId]);

  useEffect(() => {
    if (!allFavoritesData || !categoriesData) {
      return;
    }

    const allFavoritesProductCount =
      allFavoritesData?.currentStore?.favoriteVariants?.totalCount ?? 0;

    const favoriteCategoriesProductCount = (
      categoriesData?.currentStore?.categories?.nodes ?? []
    ).reduce((acc, node) => acc + (node?.products?.totalCount ?? 0), 0);

    const initialFavoritedCount = allFavoritesProductCount + favoriteCategoriesProductCount;
    const initialActiveFavoriteFilterCount =
      (isOnlyShowFavorites ? 1 : 0) + (activeCategories?.length ?? 0);

    setFavoritedCount(initialFavoritedCount);
    setActiveFavoriteFilterCount(initialActiveFavoriteFilterCount);
  }, [allFavoritesData, categoriesData]);

  const hideBrandsFilterIfSearchStartedFromBrand = useCatalogHideBrandsCheck();

  if (showOldFavorites) {
    return (
      <>
        <CurrentOffers
          isBold={isBold}
          isDrawer={isDrawer}
          isCyberMondayEnabled={isCyberMondayEnabled}
        />
        <IncludeIngredients
          isBold={isBold}
          logFilterEvent={logSearchApplyFilterEvent}
          isDefaultExpanded={!isContextual}
          isContextual={isContextual}
        />
        <ExcludeIngredients
          isBold={isBold}
          logFilterEvent={logSearchApplyFilterEvent}
          isContextual={isContextual}
        />
        {!hideBrandsFilterIfSearchStartedFromBrand && (
          <Brands
            isBold={isBold}
            logFilterEvent={logSearchApplyFilterEvent}
            isContextual={isContextual}
            contextualData={contextualDataCacheV2?.brands ?? []}
            isDefaultExpanded={isContextual}
          />
        )}
        <Form
          isBold={isBold}
          logFilterEvent={logSearchApplyFilterEvent}
          isContextual={isContextual}
          contextualData={contextualDataCacheV2?.forms ?? []}
        />
        <SupplementTypes isBold={isBold} logFilterEvent={logSearchApplyFilterEvent} />
        <HealthConditions isBold={isBold} logFilterEvent={logSearchApplyFilterEvent} />
        <Allergens
          isBold={isBold}
          logFilterEvent={logSearchApplyFilterEvent}
          isContextual={isContextual}
          contextualData={contextualDataCacheV2?.allergens ?? []}
        />
        <Demographics
          isBold={isBold}
          logFilterEvent={logSearchApplyFilterEvent}
          isContextual={isContextual}
          contextualData={contextualDataCacheV2?.demographics ?? []}
        />
        <FullscriptTested
          logFilterEvent={logSearchApplyFilterEvent}
          isContextual={isContextual}
          contextualData={contextualDataCacheV2?.fsTested ?? 0}
        />
        <Certifications
          isBold={isBold}
          logFilterEvent={logSearchApplyFilterEvent}
          isContextual={isContextual}
          contextualData={contextualDataCacheV2?.thirdPartyCertifications ?? []}
        />
        <PersonalCare isBold={isBold} logFilterEvent={logSearchApplyFilterEvent} />
        <Availability isBold={isBold} logFilterEvent={logSearchApplyFilterEvent} />
        <Favorites isBold={isBold} logFilterEvent={logSearchApplyFilterEvent} />
      </>
    );
  }

  const showFavoriteFilterAtTop = () => {
    if (activeFavoriteFilterCount > 0) {
      return (
        <Favorites
          isBold={isBold}
          logFilterEvent={logSearchApplyFilterEvent}
          isDefaultExpanded={activeFavoriteFilterCount > 0}
        />
      );
    }

    return null;
  };

  const showFavoriteFilterAtBottom = () => {
    if (activeFavoriteFilterCount === 0) {
      return <Favorites isBold={isBold} logFilterEvent={logSearchApplyFilterEvent} />;
    }

    return null;
  };

  return (
    <>
      <FullscriptCategory isBold={isBold} />
      <CurrentOffers
        isBold={isBold}
        isDrawer={isDrawer}
        isCyberMondayEnabled={isCyberMondayEnabled}
      />
      {showFavoriteFilterAtTop()}
      <IncludeIngredients
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={isContextual}
        isDefaultExpanded={
          !isContextual && (favoritedCount === 0 || activeFavoriteFilterCount === 0)
        }
      />
      <ExcludeIngredients
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={isContextual}
      />
      {!hideBrandsFilterIfSearchStartedFromBrand && (
        <Brands
          isBold={isBold}
          logFilterEvent={logSearchApplyFilterEvent}
          isContextual={isContextual}
          contextualData={contextualDataCacheV2?.brands ?? []}
          isDefaultExpanded={isContextual}
        />
      )}
      <Form
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={isContextual}
        contextualData={contextualDataCacheV2?.forms ?? []}
      />
      <Allergens
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={isContextual}
        contextualData={contextualDataCacheV2?.allergens ?? []}
      />
      <Demographics
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={isContextual}
        contextualData={contextualDataCacheV2?.demographics ?? []}
      />
      <FullscriptTested
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={isContextual}
        contextualData={contextualDataCacheV2?.fsTested ?? 0}
      />
      <Certifications
        isBold={isBold}
        logFilterEvent={logSearchApplyFilterEvent}
        isContextual={isContextual}
        contextualData={contextualDataCacheV2?.thirdPartyCertifications ?? []}
      />
      <Availability isBold={isBold} logFilterEvent={logSearchApplyFilterEvent} />
      {showFavoriteFilterAtBottom()}
    </>
  );
};

export { ClinicFiltersContent };
